import { useState, useRef, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import axios from "axios";
import DocumentCamera from "../../reusable/DocumentCamera";
import IdFrame from "../../reusable/frames/IdFrame";
import { Button } from "@nextui-org/react";
import { CameraIcon2 } from "../../icons/CameraIcon2";
import Header from "../../reusable/Header";
import { validateProcess } from "../../../store/verification.slice";
import { base64ToBlob, IS_DEV } from "../../utils";
import UploadSuccess from "../../reusable/UploadSuccess";
import { IdIcon } from "../../icons/IdIcon";
import PoweredBy from "../../reusable/PoweredBy";
import UploadError from "../../reusable/UploadError";
import DocUpLoadingPage from "../../reusable/DocUpLoadingPage";
import { useSelector } from "react-redux";
import { IdFrontGizmo } from "../../gizmos/IdFrontGizmo";
import { IdBackGizmo } from "../../gizmos/IdBackGizmo";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

function GovernmentDatabaseDocumentUpload({ database, country, verification, goBack, onComplete }) {
    const { t } = useTranslation();
    const [currentSideIndex, setCurrentSideIndex] = useState(0);
    const [fileInputs, setFileInputs] = useState([]);
    const [takePicture, setTakePicture] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadingError, setUploadingError] = useState("");
    const [capturedImage, setCapturedImage] = useState(null);
    const [rawImage, setRawImage] = useState(null);
    const fileInputRef = useRef(null);
    const { credentials } = useSelector((store) => store.verification);

    const search = window.location.search;
    const params = new URLSearchParams(search);

    useEffect(() => {
        return () => {
            if (capturedImage) URL.revokeObjectURL(capturedImage);
        };
    }, [capturedImage]);

    // Get sides from document_type
    const documentType = database?.documentType;
    const sides = documentType?.sides || [];
    const currentSide = sides[currentSideIndex];

    const onCapture = (imageSrc) => {
        const base64Data = imageSrc.replace(/^data:image\/[a-z]+;base64,/, "");
        const generatedBlob = base64ToBlob(base64Data, "image/jpeg");
        setCapturedImage(URL.createObjectURL(generatedBlob));
        setRawImage(generatedBlob);
        validateImage(generatedBlob);
    };

    const validateImage = (generatedBlob) => {
        setUploading(true);
        const docName = `${verification?.verificationId}-gov-${database.id}-${currentSide}.jpeg`;

        validateProcess(
            "GOVERNMENT_CHECK",
            [{ name: docName, blob: generatedBlob }],
            [
                {
                    fileName: docName,
                    fieldName: "GOVERNMENT_CHECK_DATA",
                    page: currentSide,
                    type: database.id,
                    country: country?.countryCode,
                    value: docName,
                },
            ],
            params.get("clientId"),
            verification?.verificationId,
            credentials?.accessToken
        )
            .then((_) => {
                setUploading(false);
                setUploadingError("");

                const inputData = {
                    document: {
                        name: docName,
                        blob: generatedBlob,
                    },
                    field: {
                        fieldName: "GOVERNMENT_CHECK_DATA",
                        fileName: docName,
                        value: docName,
                        page: currentSide,
                        type:`${database.id}-${documentType?.name}`,
                        country: country?.countryCode,
                    },
                };

                const updatedFileInputs = [...fileInputs, inputData];
                setFileInputs(updatedFileInputs);

                // Move to next side or complete
                if (currentSideIndex < sides.length - 1) {
                    // Reset for next capture
                    setTimeout(() => {
                        setCurrentSideIndex(currentSideIndex + 1);
                        setCapturedImage(null);
                        setRawImage(null);
                        setTakePicture(false);
                    }, 1000);
                } else {
                    // All sides captured, call onComplete
                    setTimeout(() => {
                        onComplete(updatedFileInputs);
                    }, 1000);
                }
            })
            .catch((err) => {
                setUploading(false);

                if (axios.isAxiosError(err)) {
                    const responseData = err.response?.data;
                    if (Array.isArray(responseData)) {
                        const messages = responseData
                            .map((error) => error?.defaultMessage || t("governmentDatabases.documentUpload.unknownError"))
                            .join("\n");
                        setUploadingError(messages);
                    } else {
                        setUploadingError(t("governmentDatabases.documentUpload.serverError"));
                    }
                } else {
                    setUploadingError(t("governmentDatabases.documentUpload.networkError"));
                }
            });
    };

    const tryAgain = () => {
        setTakePicture(false);
        setUploading(false);
        setUploadingError("");
        setCapturedImage(null);
    };

    const handleAttachFileBtnClick = () => {
        fileInputRef.current.click();
    };

    const handleImageChange = (event) => {
        const selectedFile = Array.from(event.target.files)[0];
        if (!selectedFile) return;

        if (!ALLOWED_IMAGE_TYPES.includes(selectedFile.type)) {
            event.target.value = "";
            setUploadingError(t("governmentDatabases.documentUpload.invalidFileType"));
            return;
        }

        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            event.target.value = "";
            setUploadingError(t("governmentDatabases.documentUpload.fileTooLarge"));
            return;
        }

        setCapturedImage(URL.createObjectURL(selectedFile));
        setRawImage(selectedFile);
        validateImage(selectedFile);
    };

    const getIllustration = () => {
        if (currentSide === 'BACK') {
            return <IdBackGizmo height={200} width={200} />;
        }
        return <IdFrontGizmo height={200} width={200} />;
    };

    const formatSideName = (side) => {
        if (!side) return t("governmentDatabases.documentUpload.documentFallback");
        return side.toUpperCase() === 'BACK'
            ? t("governmentDatabases.documentUpload.sideLabel.back")
            : t("governmentDatabases.documentUpload.sideLabel.front");
    };

    const handleGoBack = () => {
        if (currentSideIndex > 0) {
            // Go back to previous side
            setCurrentSideIndex(currentSideIndex - 1);
            setCapturedImage(null);
            setRawImage(null);
            setTakePicture(false);
            setFileInputs(fileInputs.slice(0, -1));
        } else {
            // Go back to database selection
            goBack();
        }
    };

    return (
        <div className="h-full w-full flex flex-col">
            {!capturedImage && (
                <div className="flex h-full flex-col relative">
                    {!takePicture && (
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                width: "100%",
                                zIndex: 1000,
                            }}
                        >
                            <Header goBack={handleGoBack} />
                        </div>
                    )}
                    {takePicture && (
                        <DocumentCamera
                            onCapture={onCapture}
                            documentType={documentType?.name || database.id}
                            overlay={IdFrame}
                            debug={IS_DEV && params.get("debug") === "true"}
                            clientId={params.get("clientId")}
                            accessToken={credentials?.accessToken}
                        />
                    )}
                    {!takePicture && (
                        <div className="flex h-full justify-between flex-col p-4 mt-14">
                            <div className="flex flex-col text-center">
                                <div className="font-normal text-xl">{documentType?.label || database?.name}</div>
                                <div className="flex flex-row justify-center">
                                    <div className="flex flex-row p-1 gap-2 bg-white items-center">
                                        <img
                                            src={country?.countryFlag}
                                            alt={country?.countryName}
                                            className="w-6 h-4 object-cover rounded-sm"
                                        />
                                        <div className="desc">{country?.countryName}</div>
                                    </div>
                                </div>
                                {sides.length > 1 && (
                                    <div className="desc text-sm mt-2">
                                        {t("governmentDatabases.documentUpload.step", { step: currentSideIndex + 1, total: sides.length })}
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col items-center">
                                {getIllustration()}
                                <div className="text-center">
                                    <div className="font-normal text-lg">
                                        {formatSideName(currentSide)}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={fileInputRef}
                                    onChange={handleImageChange}
                                    style={{ display: "none" }}
                                />
                                <Button
                                    radius="sm"
                                    startContent={<CameraIcon2 />}
                                    color="primary"
                                    onClick={() => setTakePicture(true)}
                                >
                                    {t("governmentDatabases.documentUpload.takePhoto")}
                                </Button>

                                <Button
                                    radius="sm"
                                    startContent={<IdIcon />}
                                    color="default"
                                    onClick={handleAttachFileBtnClick}
                                >
                                    {t("governmentDatabases.documentUpload.attachFile")}
                                </Button>

                                <PoweredBy />
                            </div>
                        </div>
                    )}
                </div>
            )}
            {uploading && capturedImage && (
                <DocUpLoadingPage
                    imageSrc={capturedImage}
                    label={t("governmentDatabases.documentUpload.processing")}
                />
            )}
            {uploadingError && (
                <UploadError
                    tryAgainFunc={tryAgain}
                    title={t("governmentDatabases.documentUpload.retakeTitle")}
                    desc={uploadingError}
                    imageSrc={{ type: "image", value: rawImage }}
                />
            )}
            {!uploadingError && !uploading && capturedImage && (
                <UploadSuccess message={t("governmentDatabases.documentUpload.captured")} />
            )}
        </div>
    );
}

export default GovernmentDatabaseDocumentUpload;
